'use client';

import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Calendar, ChevronLeft, ChevronRight, X } from 'lucide-react';
import { cn } from '@/core/utils/cn';
import { useTranslation } from '@/core/i18n';

/* -------------------------------------------------------------------------- */
/*  Types                                                                      */
/* -------------------------------------------------------------------------- */

interface DateRangePickerProps {
  id?: string;
  label?: string;
  startDate?: string;
  endDate?: string;
  onChange?: (start: string, end: string) => void;
  minDate?: string;
  className?: string;
}

/* -------------------------------------------------------------------------- */
/*  Constants                                                                  */
/* -------------------------------------------------------------------------- */

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
] as const;

const MONTH_SHORT = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
] as const;

const WEEKDAYS = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'] as const;

/* -------------------------------------------------------------------------- */
/*  Date helpers                                                               */
/* -------------------------------------------------------------------------- */

function toStr(y: number, m: number, d: number): string {
  return `${y}-${String(m + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
}

function parse(s: string): { y: number; m: number; d: number } | null {
  if (!s) return null;
  const [y, m, d] = s.split('-').map(Number);
  if (!y || !m || !d) return null;
  return { y, m: m - 1, d };
}

function daysIn(y: number, m: number): number {
  return new Date(y, m + 1, 0).getDate();
}

/** Monday = 0 … Sunday = 6 */
function startDow(y: number, m: number): number {
  const d = new Date(y, m, 1).getDay();
  return d === 0 ? 6 : d - 1;
}

function nightsBetween(a: string, b: string): number {
  if (!a || !b) return 0;
  return Math.max(0, Math.ceil((new Date(b).getTime() - new Date(a).getTime()) / 86_400_000));
}

function displayDate(s: string): string {
  const p = parse(s);
  if (!p) return '';
  return `${p.d} ${MONTH_SHORT[p.m]} ${p.y}`;
}

/* -------------------------------------------------------------------------- */
/*  Calendar grid                                                              */
/* -------------------------------------------------------------------------- */

interface Cell {
  key: string;
  day: number;
  dateStr: string;
  current: boolean;
  today: boolean;
  disabled: boolean;
}

function buildGrid(vy: number, vm: number, min: string | undefined, td: string): Cell[] {
  const total = daysIn(vy, vm);
  const pad = startDow(vy, vm);
  const cells: Cell[] = [];

  /* previous month */
  const py = vm === 0 ? vy - 1 : vy;
  const pm = vm === 0 ? 11 : vm - 1;
  const pDays = daysIn(py, pm);
  for (let i = pad - 1; i >= 0; i--) {
    const d = pDays - i;
    const ds = toStr(py, pm, d);
    cells.push({ key: `p${d}`, day: d, dateStr: ds, current: false, today: ds === td, disabled: !!min && ds < min });
  }

  /* current month */
  for (let d = 1; d <= total; d++) {
    const ds = toStr(vy, vm, d);
    cells.push({ key: `c${d}`, day: d, dateStr: ds, current: true, today: ds === td, disabled: !!min && ds < min });
  }

  /* next month (fill to 42) */
  const ny = vm === 11 ? vy + 1 : vy;
  const nm = vm === 11 ? 0 : vm + 1;
  const rem = 42 - cells.length;
  for (let d = 1; d <= rem; d++) {
    const ds = toStr(ny, nm, d);
    cells.push({ key: `n${d}`, day: d, dateStr: ds, current: false, today: ds === td, disabled: !!min && ds < min });
  }

  return cells;
}

/* -------------------------------------------------------------------------- */
/*  Component                                                                  */
/* -------------------------------------------------------------------------- */

export function DateRangePicker({
  id,
  label,
  startDate = '',
  endDate = '',
  onChange,
  minDate,
  className,
}: DateRangePickerProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [picking, setPicking] = useState<'start' | 'end'>('start');
  const [hover, setHover] = useState<string | null>(null);
  const [openAbove, setOpenAbove] = useState(false);

  /* view month */
  const initView = useMemo(() => {
    const p = parse(startDate);
    if (p) return { y: p.y, m: p.m };
    const now = new Date();
    return { y: now.getFullYear(), m: now.getMonth() };
  }, [startDate]);

  const [vy, setVy] = useState(initView.y);
  const [vm, setVm] = useState(initView.m);

  const [todayVal, setTodayVal] = useState('');
  useEffect(() => {
    const n = new Date();
    // eslint-disable-next-line react-hooks/set-state-in-effect -- client-only initialization from browser Date API
    setTodayVal(toStr(n.getFullYear(), n.getMonth(), n.getDate()));
  }, []);
  const { t } = useTranslation();

  const grid = useMemo(() => buildGrid(vy, vm, minDate, todayVal), [vy, vm, minDate, todayVal]);
  const nights = useMemo(() => nightsBetween(startDate, endDate), [startDate, endDate]);

  /* navigation */
  const prevMonth = useCallback(() => {
    setVm((m) => { if (m === 0) { setVy((y) => y - 1); return 11; } return m - 1; });
  }, []);
  const nextMonth = useCallback(() => {
    setVm((m) => { if (m === 11) { setVy((y) => y + 1); return 0; } return m + 1; });
  }, []);

  /* Lock body scroll on mobile when picker is open */
  useEffect(() => {
    if (!open) return;
    const isMobile = window.innerWidth < 640;
    if (!isMobile) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  /* open / close */
  const handleOpen = useCallback(() => {
    /* Detect whether to open above or below on desktop */
    if (containerRef.current && window.innerWidth >= 640) {
      const rect = containerRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      setOpenAbove(spaceBelow < 420);
    } else {
      setOpenAbove(false);
    }
    setOpen(true);
    if (startDate && !endDate) {
      setPicking('end');
    } else {
      setPicking('start');
    }
  }, [startDate, endDate]);

  const close = useCallback(() => {
    setOpen(false);
    setPicking('start');
    setHover(null);
  }, []);

  /* day click */
  const handleDay = useCallback((ds: string, disabled: boolean) => {
    if (disabled) return;
    if (picking === 'start') {
      onChange?.(ds, '');
      setPicking('end');
      setHover(null);
    } else {
      if (ds <= startDate) {
        onChange?.(ds, '');
        setHover(null);
      } else {
        onChange?.(startDate, ds);
        setPicking('start');
        setHover(null);
        setOpen(false);
      }
    }
  }, [picking, startDate, onChange]);

  /* clear */
  const handleClear = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    onChange?.('', '');
    setPicking('start');
    setHover(null);
  }, [onChange]);

  /* keyboard */
  const handleKey = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Escape') close();
  }, [close]);

  /* range end for visual preview */
  const rangeEnd = endDate || (picking === 'end' && hover && hover > startDate ? hover : '');

  return (
    <div ref={containerRef} className={cn('relative', className)} onKeyDown={handleKey}>
      {label && <label className="block text-[12px] font-medium text-hms-textSub mb-1.5">{label}</label>}

      {/* ── Trigger ── */}
      <button
        id={id}
        type="button"
        onClick={handleOpen}
        className={cn(
          'flex items-center gap-2 w-full h-9 px-3 text-left',
          'bg-hms-bg border border-hms-border rounded-lg',
          'focus:outline-none focus:ring-2 focus:ring-hms-gold/30 focus:border-hms-gold/50',
          'transition-colors duration-150',
          open && 'ring-2 ring-hms-gold/30 border-hms-gold/50',
        )}
      >
        <Calendar className="w-4 h-4 text-hms-textDim shrink-0" />
        <span className={cn('flex-1 text-[13px] truncate', startDate ? 'text-hms-text' : 'text-hms-textDim')}>
          {startDate
            ? `${displayDate(startDate)}${endDate ? ` \u2014 ${displayDate(endDate)}` : ' \u2014 Select end'}`
            : 'Select dates'}
        </span>
        {nights > 0 && (
          <span className="shrink-0 text-[10px] font-bold text-hms-teal bg-hms-teal/10 px-2 py-0.5 rounded-full border border-hms-teal/20">
            {nights}N
          </span>
        )}
        {(startDate || endDate) && (
          <span
            role="button"
            tabIndex={0}
            onClick={handleClear}
            onKeyDown={(e) => { if (e.key === 'Enter') handleClear(e as unknown as React.MouseEvent); }}
            className="shrink-0 p-0.5 rounded text-hms-textDim hover:text-hms-text transition-colors"
            aria-label={t('shared.dateRangePicker.clearDates')}
          >
            <X className="w-3.5 h-3.5" />
          </span>
        )}
      </button>

      {/* ── Dropdown ── */}
      {open && (
        <>
          {/* Backdrop: translucent on mobile for bottom-sheet, invisible on desktop */}
          <div
            id={id ? `${id}-backdrop` : undefined}
            className="fixed inset-0 z-40 bg-black/40 sm:bg-transparent"
            onClick={close}
          />

          {/* Calendar: bottom-sheet on mobile, dropdown on desktop */}
          <div
            id={id ? `${id}-calendar` : undefined}
            className={cn(
              'fixed z-50 bottom-0 left-0 right-0 max-h-[80vh] overflow-y-auto rounded-t-2xl border-t border-hms-border bg-hms-card shadow-2xl shadow-black/30 p-4 pb-6',
              'sm:absolute sm:bottom-auto sm:left-0 sm:right-auto sm:w-[304px] sm:max-w-[calc(100vw-2rem)] sm:max-h-none sm:overflow-visible sm:rounded-xl sm:border sm:border-hms-border sm:pb-4',
              openAbove ? 'sm:bottom-full sm:mb-1.5 sm:top-auto' : 'sm:mt-1.5',
            )}
          >
            {/* Mobile drag indicator */}
            <div className="flex justify-center mb-3 sm:hidden">
              <div className="w-10 h-1 rounded-full bg-hms-textDim/30" />
            </div>
            {/* Month nav */}
            <div className="flex items-center justify-between mb-3">
              <button
                id={id ? `${id}-prev` : undefined}
                type="button"
                onClick={prevMonth}
                className="w-7 h-7 rounded-lg flex items-center justify-center text-hms-textSub hover:text-hms-text hover:bg-hms-surface transition-colors"
                aria-label={t('shared.dateRangePicker.previousMonth')}
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <div className="flex items-center gap-1">
                <select
                  id={id ? `${id}-month-select` : undefined}
                  value={vm}
                  onChange={(e) => setVm(Number(e.target.value))}
                  className="appearance-none bg-transparent text-sm font-semibold text-hms-text cursor-pointer hover:text-hms-gold transition-colors focus:outline-none focus:text-hms-gold pr-0.5 text-right"
                >
                  {MONTH_NAMES.map((name, i) => (
                    <option key={i} value={i} className="bg-hms-card text-hms-text">{name}</option>
                  ))}
                </select>
                <select
                  id={id ? `${id}-year-select` : undefined}
                  value={vy}
                  onChange={(e) => setVy(Number(e.target.value))}
                  className="appearance-none bg-transparent text-sm font-semibold text-hms-text cursor-pointer hover:text-hms-gold transition-colors focus:outline-none focus:text-hms-gold"
                >
                  {Array.from({ length: 7 }, (_, i) => new Date().getFullYear() - 1 + i).map((y) => (
                    <option key={y} value={y} className="bg-hms-card text-hms-text">{y}</option>
                  ))}
                </select>
              </div>
              <button
                id={id ? `${id}-next` : undefined}
                type="button"
                onClick={nextMonth}
                className="w-7 h-7 rounded-lg flex items-center justify-center text-hms-textSub hover:text-hms-text hover:bg-hms-surface transition-colors"
                aria-label={t('shared.dateRangePicker.nextMonth')}
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>

            {/* Hint */}
            <div className="text-center mb-2.5">
              <span className={cn(
                'inline-block text-[10px] font-semibold px-3 py-1 rounded-full border',
                picking === 'start'
                  ? 'text-hms-gold bg-hms-gold/10 border-hms-gold/20'
                  : 'text-hms-teal bg-hms-teal/10 border-hms-teal/20',
              )}>
                {picking === 'start' ? 'Select check-in date' : 'Select check-out date'}
              </span>
            </div>

            {/* Weekday headers */}
            <div className="grid grid-cols-7 mb-0.5">
              {WEEKDAYS.map((d) => (
                <div key={d} className="text-center text-[10px] font-semibold text-hms-textDim py-1.5">{d}</div>
              ))}
            </div>

            {/* Day grid */}
            <div className="grid grid-cols-7">
              {grid.map((cell) => {
                const isStart = !!startDate && cell.dateStr === startDate;
                const isEnd = !!rangeEnd && cell.dateStr === rangeEnd;
                const inRange = !!startDate && !!rangeEnd && cell.dateStr > startDate && cell.dateStr < rangeEnd;

                return (
                  <button
                    // One id per day; spill-over days from the adjacent months
                    // carry their own prefix so a date never appears twice.
                    id={`${id ?? 'shared-date-range-picker'}-day-${cell.current ? '' : `${cell.key[0]}-`}${cell.dateStr}`}
                    key={cell.key}
                    type="button"
                    onClick={() => handleDay(cell.dateStr, cell.disabled)}
                    onMouseEnter={() => picking === 'end' && setHover(cell.dateStr)}
                    disabled={cell.disabled}
                    className={cn(
                      'relative h-9 w-full text-[12px] font-medium transition-all',
                      cell.current ? 'text-hms-text' : 'text-hms-textDim/40',
                      cell.disabled && 'text-hms-textDim/20 cursor-not-allowed',
                      !cell.disabled && !isStart && !isEnd && cell.current && 'hover:bg-hms-surface rounded-lg',
                      inRange && 'bg-hms-gold/[0.08]',
                      isStart && 'bg-hms-gold text-black font-bold rounded-l-lg',
                      isEnd && !isStart && 'bg-hms-gold text-black font-bold rounded-r-lg',
                      isStart && !rangeEnd && 'rounded-lg',
                      isStart && isEnd && 'rounded-lg',
                      cell.today && !isStart && !isEnd && 'ring-1 ring-hms-teal/50 rounded-lg',
                    )}
                  >
                    {cell.day}
                  </button>
                );
              })}
            </div>

            {/* Footer: night count */}
            {startDate && rangeEnd && (
              <div id={id ? `${id}-range-info` : undefined} className="mt-3 pt-3 border-t border-hms-border/50 flex items-center justify-between">
                <div className="flex items-center gap-1.5">
                  <Calendar className="w-3 h-3 text-hms-textDim" />
                  <span className="text-[10px] text-hms-textSub">
                    {displayDate(startDate)} &mdash; {displayDate(rangeEnd)}
                  </span>
                </div>
                <span className="text-[11px] font-bold text-hms-gold">
                  {nightsBetween(startDate, rangeEnd)} night{nightsBetween(startDate, rangeEnd) !== 1 ? 's' : ''}
                </span>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
